import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, Input, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogBaseComponent } from '@/app/core/components';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { parseDateOnly } from '@/app/shared/utils';

export interface ChangeControlFormData {
    id?: string;
    ccfNumber?: string;
    description?: string;
    status?: string;
    dateOpened?: Date;
    dateClosed?: Date;
}

@Component({
    selector: 'qc-add-change-control-form-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        Button,
        InputTextModule,
        DropdownModule,
        TextareaModule,
        CalendarModule,
    ],
    template: `
        <div class="w-[700px] max-w-[90vw] bg-white rounded-lg shadow-2xl" style="overflow: visible;">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ isEditMode ? 'Edit Change Control Form' : 'Add Change Control Form' }}
                </h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6 max-h-[70vh] overflow-y-visible">
                <form [formGroup]="ccfForm" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">CCF Number *</label>
                            <input pInputText formControlName="ccfNumber" placeholder="e.g., CCF-73" class="w-full" />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <p-dropdown
                                formControlName="status"
                                [options]="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select status"
                                [showClear]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Date Opened</label>
                            <p-calendar
                                formControlName="dateOpened"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Date Closed</label>
                            <p-calendar
                                formControlName="dateClosed"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            pTextarea
                            formControlName="description"
                            placeholder="Brief description of the change"
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>
                </form>
            </div>

            <div class="flex justify-end gap-3 p-6 border-t border-gray-200">
                <p-button label="Cancel" severity="secondary" variant="outlined" (click)="close()" class="mr-2" />
                <p-button
                    [label]="isEditMode ? 'Update' : 'Add'"
                    severity="primary"
                    (click)="save()"
                    [disabled]="!ccfForm.valid"
                />
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-calendar {
                z-index: 9999;
            }
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
                top: auto !important;
                left: auto !important;
            }
        `,
    ],
})
export class AddChangeControlFormDialogComponent extends DialogBaseComponent implements OnInit {
    @Input() initialData?: ChangeControlFormData;
    @Output() changeControlFormAdded = new EventEmitter<ChangeControlFormData>();

    private fb = inject(FormBuilder);

    ccfForm!: FormGroup;

    statusOptions = [
        { label: 'Open', value: 'Open' },
        { label: 'In Progress', value: 'In Progress' },
        { label: 'Under Review', value: 'Under Review' },
        { label: 'Approved', value: 'Approved' },
        { label: 'Closed', value: 'Closed' },
        { label: 'Rejected', value: 'Rejected' },
        { label: 'On Hold', value: 'On Hold' },
    ];

    get isEditMode(): boolean {
        return !!this.initialData?.id;
    }

    constructor() {
        super();
    }

    ngOnInit() {
        this.initializeForm();
        if (this.initialData) {
            this.patchFormWithInitialData();
        }
    }

    private initializeForm() {
        this.ccfForm = this.fb.group({
            ccfNumber: ['', [Validators.required]],
            description: [''],
            status: [''],
            dateOpened: [''],
            dateClosed: [''],
        });
    }

    private patchFormWithInitialData() {
        if (this.initialData) {
            this.ccfForm.patchValue({
                ...this.initialData,
                dateOpened: parseDateOnly(this.initialData.dateOpened),
                dateClosed: parseDateOnly(this.initialData.dateClosed),
            });
        }
    }

    save() {
        if (this.ccfForm.valid) {
            const formValue = this.ccfForm.value;
            const ccfData: ChangeControlFormData = {
                id: this.initialData?.id,
                ...formValue,
            };
            this.changeControlFormAdded.emit(ccfData);
            this.close(true);
        }
    }
}
